import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Router as ReactRouter,
  Route,
  RouterState,
  RedirectFunction,
  RouteProps,
} from 'react-router';
import { History, LocationState } from 'history';
import affiliate from '../modules/affiliate';
import { getTRS } from '../modules/cookies';
import { setAffiliateId, setEasyMarketingTrackId, preselectByQuery } from '../redux/actions/global';
import { trackToBunchBox } from '../modules/tracking';
import { setExperimentByQuery } from '../redux/actions/experiments';
import { AppState } from '../redux/reducers';
import { Store } from 'redux';
import { getWindow } from '../modules/helpers';
import { unsetGlobalError } from '../redux/actions/errors';
import { adjustRouteIfNecessary, getAppRoutes } from '../routes';
import LoadingIndicatorComponent from '../views/loadingIndicator/loadingIndicator';
import { forwardToDolphinCms } from 'src/redux/actions/dolphin-cms';
import { changeViewToIndustry } from 'src/redux/actions/router';

interface Props {
  store: Store<AppState>;
  history: History;
}

export const Router: React.FunctionComponent<Props> = ({ store, history }) => {
  const [routes, setRoutes] = useState<Partial<RouteProps>[]>([]);

  const onChange = useCallback(
    (_store: Store<AppState>) =>
      (prev: RouterState, next: RouterState): void => {
        const win = getWindow();
        const locationChanged = prev.location.pathname !== next.location.pathname;

        if (win && locationChanged) {
          win.scrollTo(0, 0);
        }

        _store.dispatch(unsetGlobalError());
      },
    [store]
  );

  const onEnter = useCallback(
    (nextState: RouterState & LocationState, replace: RedirectFunction) => {
      const state = store.getState();
      const { query, hash } = nextState.location;
      const ref = affiliate(query, hash);
      const trsId = getTRS();
      if (ref) {
        store.dispatch(setAffiliateId(ref));
      }

      if (trsId) {
        store.dispatch(setEasyMarketingTrackId(trsId));
      }

      if (query.product === 'dolphin') {
        store.dispatch(forwardToDolphinCms());
      } else if (query.product === 'creator') {
        store.dispatch(changeViewToIndustry());
      }
      trackToBunchBox('evaluate experiments');

      store.dispatch(preselectByQuery(query));
      store.dispatch(setExperimentByQuery(query));
      adjustRouteIfNecessary(state, nextState, replace);
    },
    [store]
  );

  useEffect(() => {
    getAppRoutes(store).then((_routes) => {
      setRoutes(_routes);
    });
  }, []);

  if (routes.length === 0) {
    return <LoadingIndicatorComponent />;
  }

  return (
    <ReactRouter history={history}>
      <Route onEnter={onEnter} onChange={onChange(store)}>
        {routes.map((route) => {
          return (
            <Route
              path={route.path}
              component={(props) => {
                const View = route.component;
                return <View {...props} />;
              }}
              onEnter={route.onEnter}
              key={route.path}
            />
          );
        })}
      </Route>
    </ReactRouter>
  );
};
